import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, FleetStatusBadge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, KeyValue } from '../../components/Card';
import { Select } from '../../components/Field';
import { useFleetStore } from '../fleet/fleetStore';
import { droneAircraftLabel, droneFleetStatus } from '../fleet/status';
import { useDroneLink, useLiveTick, useTrailSession } from '../../drone/hooks';
import { useBreakpoint, useMobileGateBypass } from '../../hooks/useBreakpoint';
import { OverlayScopeBoundary } from '../../overlay';
import { toast } from '../../components/Toast';
import { batteryTone, linkQualityTone } from '../../lib/droneStatus';
import { AlertBanner, LINK_PHASE_LABELS } from './components/AlertBanner';
import { AttitudeBall } from './components/AttitudeBall';
import { BigMetric } from './components/LiveMetric';
import { CommandBar } from './components/CommandBar';
import { MiniMap } from './components/MiniMap';
import { MobileGate } from './components/MobileGate';
import { TelemetryTable } from './components/TelemetryTable';
import {
  FLIGHT_MODE_LABELS,
  GPS_FIX_LABELS,
  MISSION_STATE_LABELS,
  formatAltitude,
  formatCoord,
  formatDistance,
  formatDuration,
  formatDurationCompact,
  formatNumber,
  formatRelativeTime,
  formatSpeed,
} from '@shared/format';
import { resolveThresholds } from '@shared/defaults';

/**
 * 单机管理。
 *
 * 硬实时约束体现在三处：
 *   1. 所有高频读数（姿态、大数字）绕开 React，直接写 DOM（见 LiveMetric / AttitudeBall）；
 *   2. 本页运行在 `realtime` 浮层作用域下，任何弹窗都不得出现背景蒙版；
 *   3. 不做移动端适配 —— 窄屏直接不渲染内容，只给替代入口。
 */
export function TrackingPage() {
  // 硬实时作用域：本页内所有功能性弹窗都会降级为停靠面板，不再出现背景蒙版。
  return (
    <OverlayScopeBoundary scope="realtime">
      <TrackingPageContent />
    </OverlayScopeBoundary>
  );
}

function TrackingPageContent() {
  const { droneId } = useParams<{ droneId?: string }>();
  const navigate = useNavigate();
  const drones = useFleetStore((state) => state.drones);
  const loaded = useFleetStore((state) => state.loaded);
  const load = useFleetStore((state) => state.load);
  const { isMobile, isTablet } = useBreakpoint();
  const { bypassed, bypass } = useMobileGateBypass();
  const now = useLiveTick(1000);

  useEffect(() => {
    void load();
  }, [load]);

  const drone = useMemo(
    () => drones.find((item) => item.id === droneId) ?? null,
    [drones, droneId],
  );

  const gated = (isMobile || isTablet) && !bypassed;
  // 被拦截时不订阅链路，避免在移动端白白建立 WebSocket 连接
  const entry = useDroneLink(gated ? null : drone);
  // 离开本页即丢弃尾迹，重新进入时从当前机位重新累积
  useTrailSession();

  const telemetry = entry?.telemetry ?? null;
  const link = entry?.link ?? null;
  const alerts = entry?.alerts ?? null;
  const thresholds = resolveThresholds(drone?.thresholds);
  const status = drone ? droneFleetStatus(drone, entry, now) : null;
  const [autoSelectApplied, setAutoSelectApplied] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // 全屏把整页（含指令面板、完整数据表）投到顶层窗口，监管员可以无干扰盯机。
  // 用 fullscreenchange 而非本地状态，保证按 Esc / F11 退出后按钮状态同步。
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // 未指定机号时自动选中第一台启用中的设备，避免进入空白页
  useEffect(() => {
    if (droneId || gated || autoSelectApplied || drones.length === 0) return;
    const first = drones.find((item) => item.enabled) ?? drones[0];
    if (first) {
      setAutoSelectApplied(true);
      navigate(`/fleet/tracking/${first.id}`, { replace: true });
    }
  }, [droneId, gated, autoSelectApplied, drones, navigate]);

  if (gated) return <MobileGate onBypass={bypass} />;

  if (loaded && drones.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          title="还没有注册无人机"
          description="单机管理需要先在南向设备管理中注册无人机并获得接入凭据。"
          action={
            <Button variant="primary" onClick={() => navigate('/fleet/drones')}>
              前往无人机管理
            </Button>
          }
        />
      </div>
    );
  }

  if (!drone) {
    return (
      <div className="p-4">
        <EmptyState
          title="未选择无人机"
          description="请选择一台无人机进入单机管理。"
          action={
            <Button onClick={() => navigate('/fleet')}>返回总览</Button>
          }
        />
      </div>
    );
  }

  const supportedCommands = link?.capabilities?.commands ?? null;
  // now 来自 1Hz 的 useLiveTick，可能比刚写入的帧时间还早，钳到 0 免得出现「-0.4 s」的读数
  const frameAgeMs = telemetry ? Math.max(0, now - telemetry.receivedAt) : null;

  async function toggleFullscreen() {
    const stage = stageRef.current;
    if (!stage) return;
    try {
      if (document.fullscreenElement === stage) {
        await document.exitFullscreen();
        return;
      }
      if (typeof stage.requestFullscreen !== 'function') {
        toast.warning('当前浏览器不支持网页全屏', '请使用桌面版 Chrome / Edge / Safari 16+');
        return;
      }
      await stage.requestFullscreen();
    } catch (cause) {
      toast.critical('全屏切换失败', cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <div
      ref={stageRef}
      data-realtime
      className="h-full overflow-auto bg-surface-0"
      // 强制渲染时按电脑端固定宽度呈现，宁可横向滚动也不压缩读数区域
      style={{ minWidth: '1024px' }}
    >
      <div className="flex flex-col gap-3 p-3">
        <header className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-lg font-bold text-fg">{droneAircraftLabel(drone)}</h1>
          <span className="font-mono text-xs text-fg-muted">{drone.uniqueId}</span>
          {status ? <FleetStatusBadge status={status} /> : null}
          {telemetry ? (
            <Badge tone="neutral">
              {FLIGHT_MODE_LABELS[telemetry.flight.mode] ?? telemetry.flight.mode}
            </Badge>
          ) : null}
          {telemetry ? (
            <Badge tone={telemetry.flight.armed ? 'warning' : 'neutral'}>
              电机{telemetry.flight.armed ? '已解锁' : '已上锁'}
            </Badge>
          ) : null}
          <Badge tone="neutral">
            {link ? LINK_PHASE_LABELS[link.phase] : '未连接'}
            {link?.transport === 'ws' ? ' · WebSocket' : link?.transport === 'poll' ? ' · REST 轮询' : ''}
          </Badge>
          <Badge tone={frameAgeMs !== null && frameAgeMs <= drone.staleAfterMs ? 'ok' : 'critical'}>
            {frameAgeMs === null ? '无数据' : `数据延迟 ${(frameAgeMs / 1000).toFixed(1)} s`}
          </Badge>

          <div className="ml-auto flex items-center gap-2">
            <div className="w-64">
              <Select
                aria-label="选择无人机"
                options={drones.map((item) => ({
                  value: item.id,
                  label: `${droneAircraftLabel(item)}${item.enabled ? '' : '（已停用）'}`,
                }))}
                value={drone.id}
                onChange={(event) => navigate(`/fleet/tracking/${event.target.value}`)}
              />
            </div>
            <Button
              title={fullscreen ? '退出全屏（Esc）' : '全屏盯机：隐藏平台导航，只保留本机遥测与指令面板'}
              aria-pressed={fullscreen}
              onClick={() => void toggleFullscreen()}
              icon={<FullscreenIcon exit={fullscreen} />}
            >
              {fullscreen ? '退出全屏' : '全屏'}
            </Button>
            <Button onClick={() => navigate('/fleet')}>返回总览</Button>
          </div>
        </header>

        <AlertBanner
          evaluation={alerts}
          link={link}
          frameAgeMs={frameAgeMs}
          staleAfterMs={drone.staleAfterMs}
        />

        <section className="grid grid-cols-2 gap-2.5 lg:grid-cols-4 xl:grid-cols-4">
          <BigMetric
            label="电池电量"
            size="xl"
            emphasis
            progress
            droneId={drone.id}
            telemetry={telemetry}
            unit="%"
            select={(frame) => ({
              text: formatNumber(Math.round(frame.battery.percent)),
              tone: batteryTone(frame.battery.percent),
              progress: frame.battery.percent / 100,
            })}
            hint={
              telemetry
                ? `${telemetry.battery.voltageV.toFixed(1)} V · ${telemetry.battery.currentA.toFixed(1)} A`
                : undefined
            }
          />
          <BigMetric
            label="信号质量"
            size="xl"
            emphasis
            droneId={drone.id}
            telemetry={telemetry}
            unit="/100"
            select={(frame) => ({
              text: String(Math.round(frame.link.quality)),
              tone: linkQualityTone(frame.link.quality),
            })}
            hint={telemetry ? `${telemetry.link.rssiDbm.toFixed(0)} dBm` : undefined}
          />
          <BigMetric
            label="光流质量"
            size="xl"
            emphasis
            droneId={drone.id}
            telemetry={telemetry}
            unit="/100"
            select={(frame) => ({
              text: String(Math.round(frame.opticalFlow.quality)),
              tone: linkQualityTone(frame.opticalFlow.quality),
            })}
            hint={telemetry ? (telemetry.opticalFlow.enabled ? `高度 ${telemetry.opticalFlow.heightM.toFixed(1)} m` : '光流未启用') : undefined}
          />
          <BigMetric
            label="可见卫星"
            size="xl"
            emphasis
            droneId={drone.id}
            telemetry={telemetry}
            unit="颗"
            select={(frame) => ({
              text: String(frame.gps.satellites),
              tone: frame.gps.satellites >= 12 ? 'ok' : frame.gps.satellites >= 8 ? 'caution' : 'critical',
            })}
            hint={telemetry ? GPS_FIX_LABELS[telemetry.gps.fixType] : undefined}
          />
        </section>

        <section className="grid grid-cols-2 gap-2.5 lg:grid-cols-3 xl:grid-cols-6">
          <BigMetric
            label="相对高度"
            droneId={drone.id}
            telemetry={telemetry}
            unit="m"
            size="lg"
            select={(frame) => ({ text: frame.position.altM.toFixed(1) })}
          />
          <BigMetric
            label="地速"
            droneId={drone.id}
            telemetry={telemetry}
            unit="m/s"
            size="lg"
            select={(frame) => ({ text: frame.velocity.groundSpeedMs.toFixed(1) })}
          />
          <BigMetric
            label="垂直速度"
            droneId={drone.id}
            telemetry={telemetry}
            unit="m/s"
            size="lg"
            select={(frame) => ({
              text: frame.velocity.verticalSpeedMs.toFixed(1),
              tone:
                Math.abs(frame.velocity.verticalSpeedMs) > 5
                  ? 'caution'
                  : undefined,
            })}
          />
          <BigMetric
            label="距起飞点"
            droneId={drone.id}
            telemetry={telemetry}
            unit="m"
            size="lg"
            select={(frame) => ({ text: frame.position.homeDistanceM.toFixed(0) })}
          />
          <BigMetric
            label="剩余续航"
            droneId={drone.id}
            telemetry={telemetry}
            size="lg"
            select={(frame) => ({ text: formatDurationCompact(frame.battery.remainingSec) })}
            hint={telemetry ? `链路延迟 ${telemetry.link.latencyMs.toFixed(0)} ms` : undefined}
          />
          <BigMetric
            label="丢包率"
            droneId={drone.id}
            telemetry={telemetry}
            unit="%"
            size="lg"
            select={(frame) => ({
              text: frame.link.packetLossPct.toFixed(1),
              tone:
                frame.link.packetLossPct > 5
                  ? 'critical'
                  : frame.link.packetLossPct > 1
                    ? 'caution'
                    : 'ok',
            })}
            hint={link ? `丢帧 ${link.droppedFrames} · 帧数 ${link.frameCount}` : undefined}
          />
        </section>

        <section className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <Card className="xl:col-span-4">
            <CardHeader>
              <CardTitle subtitle="滚转 −90° ~ +90°，俯仰 ±90°">实时姿态</CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col items-center gap-3">
              <AttitudeBall droneId={drone.id} telemetry={telemetry} size={232} />
              <div className="grid w-full grid-cols-3 gap-2">
                <BigMetric
                  label="横滚"
                  droneId={drone.id}
                  telemetry={telemetry}
                  unit="°"
                  select={(frame) => ({ text: frame.attitude.rollDeg.toFixed(1) })}
                />
                <BigMetric
                  label="俯仰"
                  droneId={drone.id}
                  telemetry={telemetry}
                  unit="°"
                  select={(frame) => ({ text: frame.attitude.pitchDeg.toFixed(1) })}
                />
                <BigMetric
                  label="航向"
                  droneId={drone.id}
                  telemetry={telemetry}
                  unit="°"
                  select={(frame) => ({ text: frame.attitude.yawDeg.toFixed(1) })}
                />
              </div>
              <KeyValue
                columns={3}
                items={[
                  { label: '滚转速率', value: telemetry ? `${telemetry.attitude.rollRateDegS.toFixed(1)} °/s` : '--' },
                  { label: '俯仰速率', value: telemetry ? `${telemetry.attitude.pitchRateDegS.toFixed(1)} °/s` : '--' },
                  { label: '航向速率', value: telemetry ? `${telemetry.attitude.yawRateDegS.toFixed(1)} °/s` : '--' },
                ]}
              />
            </CardBody>
          </Card>

          <Card className="xl:col-span-5">
            <CardHeader
              actions={
                telemetry ? (
                  <span className="tabular text-xs text-fg-muted">
                    {formatCoord(telemetry.position.lat, telemetry.position.lng)}
                  </span>
                ) : null
              }
            >
              <CardTitle subtitle="航迹为浏览器侧实时记录，不落库">实时位置</CardTitle>
            </CardHeader>
            <CardBody className="p-2">
              <MiniMap
                droneId={drone.id}
                name={droneAircraftLabel(drone)}
                telemetry={telemetry}
                status={status ?? 'offline'}
                className="h-[360px] rounded border border-line"
              />
            </CardBody>
          </Card>

          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle subtitle="链路与设备">链路 / 机型</CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col gap-3">
              <KeyValue
                items={[
                  { label: '链路状态', value: link ? LINK_PHASE_LABELS[link.phase] : '未连接' },
                  { label: '传输方式', value: link?.transport === 'ws' ? 'WebSocket' : link?.transport === 'poll' ? 'REST 轮询' : '--' },
                  { label: '最近帧', value: telemetry ? formatRelativeTime(telemetry.receivedAt, now) : '--' },
                  { label: '帧序号', value: telemetry ? String(telemetry.seq) : '--' },
                  { label: '丢帧数', value: link ? String(link.droppedFrames) : '--' },
                  { label: '链路延迟', value: telemetry ? `${telemetry.link.latencyMs.toFixed(0)} ms` : '--' },
                ]}
              />

              <div className="border-t border-line pt-2.5">
                <KeyValue
                  items={[
                    { label: '机型', value: drone.model || '--' },
                    { label: '厂商', value: drone.vendor || '--' },
                    { label: '接入地址', value: `${drone.endpoint.protocol}://${drone.endpoint.host}:${drone.endpoint.port}` },
                    { label: '遥测频率', value: `${drone.telemetryRateHz} Hz` },
                    { label: '失联阈值', value: `${Math.round(drone.staleAfterMs / 1000)} s` },
                    { label: '适配器', value: drone.adapterProfile || '默认（canonical）' },
                  ]}
                />
              </div>

              <div className="border-t border-line pt-2.5">
                <KeyValue
                  items={[
                    ...(telemetry
                      ? [
                          { label: '飞行模式', value: FLIGHT_MODE_LABELS[telemetry.flight.mode] ?? telemetry.flight.mode },
                          { label: '任务状态', value: MISSION_STATE_LABELS[telemetry.flight.missionState] ?? telemetry.flight.missionState },
                          { label: '电量告警阈值', value: `${thresholds.batteryPercentCaution}% / ${thresholds.batteryPercentWarning}% / ${thresholds.batteryPercentCritical}%` },
                          { label: '海拔高度', value: formatAltitude(telemetry.position.altAbsM) },
                          { label: '距起飞点', value: formatDistance(telemetry.position.homeDistanceM) },
                          { label: '环境温度', value: `${telemetry.environment.temperatureC.toFixed(1)} °C` },
                          { label: '风速', value: formatSpeed(telemetry.environment.windSpeedMs) },
                          { label: '剩余续航', value: formatDuration(telemetry.battery.remainingSec) },
                        ]
                      : []),
                    {
                      label: '能力清单',
                      value: link?.capabilities?.commands?.length
                        ? `${link.capabilities.commands.length} 条指令`
                        : '未上报（按平台全量展示）',
                    },
                  ]}
                />
              </div>

              {link?.issues.length ? (
                <ul className="flex flex-col gap-1 rounded border border-caution bg-caution-soft px-2.5 py-2">
                  {link.issues.map((issue) => (
                    <li key={issue} className="text-[11px] text-fg">
                      {issue}
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardBody>
          </Card>
        </section>

        <CommandBar
          droneId={drone.id}
          telemetry={telemetry}
          link={link}
          staleAfterMs={drone.staleAfterMs}
          supportedCommands={supportedCommands}
        />

        <Card>
          <CardHeader>
            <CardTitle subtitle="按分组展开全部规范字段，异常字段会单独提示">完整数据列表</CardTitle>
          </CardHeader>
          <CardBody className="p-2.5">
            <TelemetryTable
              droneId={drone.id}
              telemetry={telemetry}
              issues={link?.issues ?? []}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

/** 全屏/退出全屏图标：四角箭头朝外表示进入，朝内表示退出。 */
function FullscreenIcon({ exit }: { exit: boolean }) {
  const d = exit
    ? 'M10 4H4v6h2.5V6.5H10V4Zm4 0h6v6h-2.5V6.5H14V4Zm-10 10h6v6h-2.5V16.5H4V14Zm10 0h6v6h-2.5v-3.5H14v-2.5Z'
    : 'M4 4h6v2.5H6.5V10H4V4Zm10 0h6v6h-2.5V6.5H14V4ZM4 14h2.5v3.5H10V20H4v-6Zm13.5 0H20v6h-6v-2.5h3.5V14Z';
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-3.5 shrink-0">
      <path d={d} fill="currentColor" />
    </svg>
  );
}

